import { cssVar } from '@mfw/common';

import type { OnSurfaceFamily } from '../../onSurface';

import { ON_SURFACE_FAMILIES, onSurface } from '../../onSurface';
import { fillMain } from '../../fillMain';
import type { ButtonProps } from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';

import { buttonClasses } from '@mui/material/Button';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = [
  'primary',
  'accent',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
    /**
     * The brand gradient CTA — one per screen, for the action the screen
     * exists for. Takes no `color`: the gradient IS the brand, and whether it
     * may run at all is decided by measurement in `applyThemeToRoot`.
     */
    gradient: true;
  }
  // NEW COLOUR — the second brand anchor. MUI resolves `palette[color]`
  // dynamically, so the runtime needs nothing beyond the `accent` slot
  // `theme/palette.ts` adds; this is what lets a call site NAME it.
  interface ButtonPropsColorOverrides {
    accent: true;
  }
}

/**
 * IconButton has to accept the colour too, and NOT because it is styled here.
 *
 * `components/Menu` is generic over one props type (`MenuProps extends
 * ButtonProps`) and is instantiated twice — `ButtonMenu = Menu(Button)` and
 * `IconButtonMenu = Menu(IconButton)`. Widening `ButtonProps['color']` alone
 * widens `MenuProps` with it, and the IconButton instantiation then stops
 * satisfying its own parameter type. So the two colour unions are coupled by
 * that file, and a colour added to one must be added to the other.
 *
 * It is the right answer on its own terms as well: an icon button is as
 * entitled to the accent as a text one, and MUI resolves its colour through
 * the same `palette[color].main` lookup.
 */
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    accent: true;
  }
}

// ----------------------------------------------------------------------

export function button(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const gradientVariant = ownerState.variant === 'gradient';

    const smallSize = ownerState.size === 'small';

    const mediumSize = ownerState.size === 'medium';

    const largeSize = ownerState.size === 'large';

    const defaultStyle = {
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          '&:hover': {
            backgroundColor: lightMode
              ? theme.palette.grey[700]
              : theme.palette.grey[400],
          },
          backgroundColor: lightMode
            ? theme.palette.grey[800]
            : theme.palette.common.white,
          color: lightMode
            ? theme.palette.common.white
            : theme.palette.grey[800],
        }),
        // OUTLINED
        ...(outlinedVariant && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          borderColor: alpha(theme.palette.grey[500], 0.32),
        }),
        // TEXT
        ...(textVariant && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // SOFT
        ...(softVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.24),
          },
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          color: cssVar.text.primary,
        }),
      }),
      ...(outlinedVariant && {
        '&:hover': {
          borderColor: 'currentColor',
          boxShadow: '0 0 0 0.5px currentColor',
        },
      }),
      // GRADIENT — outside the colour loop below on purpose: this variant takes
      // no `color`, because the gradient IS the brand and a `color="error"`
      // gradient would be a green-to-azure "Delete".
      //
      // It names three variables and no colour of its own:
      //   `--mfw-cta-fill` is the gradient, or a solid `primary-main` when the
      //   two stops cannot carry one ink — `applyThemeToRoot` measures that,
      //   since CSS cannot;
      //   `--mfw-cta-ink` is the brand's derived `contrastText`, right for
      //   either outcome;
      //   `--mfw-glow-brand` is the effect token, so the organization's glow
      //   setting reaches this button without it knowing the setting exists.
      ...(gradientVariant && {
        '&:hover': {
          // The fill does not move on hover — a gradient that shifts reads as a
          // repaint rather than as feedback. The light does.
          background: 'var(--mfw-cta-fill)',
          filter: 'brightness(1.08)',
        },
        background: 'var(--mfw-cta-fill)',
        boxShadow: 'var(--mfw-glow-brand)',
        color: 'var(--mfw-cta-ink)',
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        // CONTAINED — the hover keeps `main`. MUI moves it to `.dark`, but the
        // label is `contrastText`, derived against `main` only: for a mid-tone
        // brand under black ink the darker step read 2.63–2.69:1. The light is
        // the feedback, as on the gradient CTA (`fill-hover.spec.ts`).
        ...(containedVariant && {
          '&:hover': {
            backgroundColor: fillMain(theme, color),
            boxShadow: theme.customShadows[color],
          },
        }),
        // OUTLINED / TEXT — no fill, so the label (and the border) ARE the
        // colour drawn on the surface. MUI's default is `<color>.main`, picked
        // to stand out as a FILL under `contrastText`: as ink it measured
        // 2.62–2.90:1 on a cream tenant, and these carry the destructive
        // actions. The on-surface alias is the same colour at the step that
        // reads on whichever surface is current. Same rule the outlined Chip
        // already follows.
        ...((outlinedVariant || textVariant) &&
          ON_SURFACE_FAMILIES.includes(color as OnSurfaceFamily) && {
            color: onSurface[color as OnSurfaceFamily],
            ...(outlinedVariant && {
              borderColor: onSurface[color as OnSurfaceFamily],
            }),
          }),
        // SOFT
        ...(softVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.32),
          },
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          color: theme.palette[color][lightMode ? 'dark' : 'light'],
        }),
      }),
    }));

    const disabledState = {
      [`&.${buttonClasses.disabled}`]: {
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
      },
    };

    const size = {
      ...(smallSize && {
        fontSize: 13,
        height: 30,
        paddingLeft: 8,
        paddingRight: 8,
        ...(textVariant && {
          paddingLeft: 4,
          paddingRight: 4,
        }),
      }),
      ...(mediumSize && {
        paddingLeft: 12,
        paddingRight: 12,
        ...(textVariant && {
          paddingLeft: 8,
          paddingRight: 8,
        }),
      }),
      ...(largeSize && {
        fontSize: 15,
        height: 48,
        paddingLeft: 16,
        paddingRight: 16,
        ...(textVariant && {
          paddingLeft: 10,
          paddingRight: 10,
        }),
      }),
    };

    return [defaultStyle, ...colorStyle, disabledState, size];
  };

  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
